import { Box, Button, Typography, Fade } from '@mui/material';

/**
 * RondaDecisionButtons - Botones para las decisiones de Ronda/Rondín y envíos
 *
 * Se muestran en la parte inferior derecha de la pantalla cuando el jugador 1
 * tiene que tomar una decisión sobre su Ronda/Rondín o responder a un envío.
 *
 * Decisiones posibles:
 * - Cantar Ronda/Rondín (cuando tiene una)
 * - No Cantar (pasar sin declarar la ronda)
 * - Enviar (apostar la Ronda)
 * - Chiquita (pasar la decisión al siguiente)
 * - Acepto (aceptar el envío del rival)
 * - No Acepto (rechazar el envío)
 * - A la mayor (resolver sin apostar, gana la mayor)
 */
const RondaDecisionButtons = ({
    visible = false,
    decisions = [], // Array de decisiones disponibles: ['cantar', 'no_cantar', 'enviar', 'chiquita', 'acepto', 'no_acepto', 'a_la_mayor']
    declaration = null, // { type: 'ronda'|'rondin', value: number }
    onDecision, // (decision) => void
    disabled = false,
    playerName = 'Jugador'
}) => {
    if (!visible || decisions.length === 0) {
        return null;
    }

    const getButtonStyle = (decision) => {
        const baseStyle = {
            fontWeight: 'bold',
            textTransform: 'none',
            fontSize: '1rem',
            padding: '10px 20px',
            borderRadius: '12px',
            minWidth: '120px',
            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
            transition: 'all 0.2s ease',
            '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 20px rgba(0, 0, 0, 0.3)',
            },
            '&:disabled': {
                opacity: 0.5,
            }
        };

        switch (decision) {
            case 'cantar':
                return {
                    ...baseStyle,
                    background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                    color: '#1a1a1a',
                    '&:hover': {
                        ...baseStyle['&:hover'],
                        background: 'linear-gradient(135deg, #FFE44D 0%, #FFB733 100%)',
                    }
                };
            case 'no_cantar':
                return {
                    ...baseStyle,
                    background: 'linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%)',
                    color: 'white',
                    '&:hover': {
                        ...baseStyle['&:hover'],
                        background: 'linear-gradient(135deg, #bdc3c7 0%, #95a5a6 100%)',
                    }
                };
            case 'enviar':
                return {
                    ...baseStyle,
                    background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
                    color: 'white',
                    '&:hover': {
                        ...baseStyle['&:hover'],
                        background: 'linear-gradient(135deg, #ec7063 0%, #e74c3c 100%)',
                    }
                };
            case 'chiquita':
                return {
                    ...baseStyle,
                    background: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
                    color: 'white',
                    '&:hover': {
                        ...baseStyle['&:hover'],
                        background: 'linear-gradient(135deg, #5dade2 0%, #3498db 100%)',
                    }
                };
            case 'acepto':
                return {
                    ...baseStyle,
                    background: 'linear-gradient(135deg, #27ae60 0%, #1e8449 100%)',
                    color: 'white',
                    '&:hover': {
                        ...baseStyle['&:hover'],
                        background: 'linear-gradient(135deg, #2ecc71 0%, #27ae60 100%)',
                    }
                };
            case 'no_acepto':
                return {
                    ...baseStyle,
                    background: 'linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%)',
                    color: 'white',
                    '&:hover': {
                        ...baseStyle['&:hover'],
                        background: 'linear-gradient(135deg, #bdc3c7 0%, #95a5a6 100%)',
                    }
                };
            case 'a_la_mayor':
                return {
                    ...baseStyle,
                    background: 'linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%)',
                    color: 'white',
                    '&:hover': {
                        ...baseStyle['&:hover'],
                        background: 'linear-gradient(135deg, #af7ac5 0%, #9b59b6 100%)',
                    }
                };
            default:
                return baseStyle;
        }
    };

    const getButtonLabel = (decision) => {
        switch (decision) {
            case 'cantar':
                if (declaration) {
                    return declaration.type === 'rondin' ? '¡RONDÍN!' : '¡RONDA!';
                }
                return '¡CANTAR!';
            case 'no_cantar':
                return 'No Cantar';
            case 'enviar':
                return 'Enviar';
            case 'chiquita':
                return 'Chiquita';
            case 'acepto':
                return 'Acepto';
            case 'no_acepto':
                return 'No Acepto';
            case 'a_la_mayor':
                return 'A la Mayor';
            default:
                return decision;
        }
    };

    const getDeclarationLabel = () => {
        if (!declaration) return null;
        // No revelar el valor de la ronda, solo el tipo
        return declaration.type === 'rondin' ? 'Rondín' : 'Ronda';
    };

    return (
        <Fade in={visible}>
            <Box
                sx={{
                    position: 'fixed',
                    bottom: 20,
                    right: 20,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    gap: 1.5,
                    zIndex: 1000,
                    maxWidth: '300px',
                }}
            >
                {/* Header con información */}
                <Box
                    sx={{
                        background: 'rgba(0, 0, 0, 0.85)',
                        backdropFilter: 'blur(10px)',
                        borderRadius: '12px',
                        padding: '12px 16px',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.4)',
                    }}
                >
                    <Typography
                        sx={{
                            color: 'white',
                            fontSize: '0.9rem',
                            fontWeight: 'bold',
                            textAlign: 'center',
                        }}
                    >
                        {declaration ? `Tienes ${getDeclarationLabel()}` : 'Decisión'}
                    </Typography>
                    <Typography
                        sx={{
                            color: 'rgba(255, 255, 255, 0.7)',
                            fontSize: '0.75rem',
                            textAlign: 'center',
                            marginTop: '4px',
                        }}
                    >
                        {playerName}, elige una opción:
                    </Typography>
                </Box>

                {/* Botones de decisión */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        alignItems: 'flex-end',
                    }}
                >
                    {decisions.map((decision) => (
                        <Button
                            key={decision}
                            onClick={() => onDecision(decision)}
                            disabled={disabled}
                            sx={getButtonStyle(decision)}
                        >
                            {getButtonLabel(decision)}
                        </Button>
                    ))}
                </Box>
            </Box>
        </Fade>
    );
};

export default RondaDecisionButtons;
